// src/components/Colaborador/TermometroHumor.js

import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { db } from '../../firebaseConfig';
import { 
    doc, 
    setDoc, 
    arrayUnion, 
    collection, 
    addDoc, 
    Timestamp, 
    query, 
    where, 
    getDocs, 
    getDoc 
} from 'firebase/firestore';
import { FaPaperPlane } from 'react-icons/fa';

// --- COMPONENTE DE PAGINAÇÃO INTERNO ---
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  const pageNumbersToDisplay = [];
  const sideWidth = 1;
  const maxVisiblePageNumbers = (sideWidth * 2) + 3;

  if (totalPages <= maxVisiblePageNumbers) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbersToDisplay.push(i);
    }
  } else {
    const pages = new Set();
    pages.add(1);
    pages.add(totalPages);
    for (let i = Math.max(1, currentPage - sideWidth); i <= Math.min(totalPages, currentPage + sideWidth); i++) {
      pages.add(i);
    }
    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    let lastPageAdded = 0;
    for (const page of sortedPages) {
      if (lastPageAdded !== 0) {
        if (page - lastPageAdded > 2) {
          pageNumbersToDisplay.push('...');
        } else if (page - lastPageAdded === 2) {
          pageNumbersToDisplay.push(lastPageAdded + 1);
        }
      }
      pageNumbersToDisplay.push(page);
      lastPageAdded = page;
    }
  }

  return (
    <nav>
      <ul className="pagination-wrapper">
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            Anterior
          </button>
        </li>
        {pageNumbersToDisplay.map((page, index) => {
          if (page === '...') {
            return <li key={`ellipsis-${index}`} className="page-item disabled"><span className="page-link">...</span></li>;
          }
          return (
            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(page)}>
                {page}
              </button>
            </li>
          );
        })}
        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Próxima
          </button>
        </li>
      </ul>
    </nav>
  );
};

// --- HELPER DE DATA ---
const getJsDate = (timestampField) => {
    if (!timestampField) return null;
    if (typeof timestampField.toDate === 'function') {
        return timestampField.toDate();
    }
    if (timestampField instanceof Date) {
        return timestampField;
    }
    try {
        let dateStr = String(timestampField);
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dateStr += 'T00:00:00Z';
        }
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
        }
    } catch (e) { /* Retorna null */ }
    return null;
};

// --- COMPONENTE PRINCIPAL ---
const TermometroHumor = ({
    historicoHumor,
    userId,
    isReadOnly = false,
    userData,
    empresaId,
    isModalMode = false,
    onSurveySubmit,
    campaignId,
    hideTitle = false
}) => {
  const [humorSelecionado, setHumorSelecionado] = useState('');
  const [observacao, setObservacao] = useState('');
  const [mensagem, setMensagem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Histórico Local e Paginação
  const [localHistorico, setLocalHistorico] = useState(() =>
    Array.isArray(historicoHumor) ? historicoHumor.map(h => ({...h, registradoEm: getJsDate(h.registradoEm)})) : []
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [dynamicItemsPerPage, setDynamicItemsPerPage] = useState(5);
  
  const historyListRef = useRef(null);
  const sampleListItemRef = useRef(null);

  // Atualiza histórico quando props mudam
  useEffect(() => {
    setLocalHistorico(Array.isArray(historicoHumor) ? historicoHumor.map(h => ({...h, registradoEm: getJsDate(h.registradoEm)})) : []);
  }, [historicoHumor]);

  const sortedHistorico = useMemo(() => {
    return [...localHistorico].sort((itemA, itemB) => {
        const dateA = itemA.registradoEm;
        const dateB = itemB.registradoEm;
        if (!dateA || !dateB) return 0;
        return dateB.getTime() - dateA.getTime();
    });
  }, [localHistorico]);

  // Cálculo dinâmico de itens por página baseado na altura disponível (se não for modal)
  const calculateItemsPerPage = useCallback(() => {
    if (isModalMode) {
        setDynamicItemsPerPage(100);
        return;
    }
    const listNode = historyListRef.current;
    const sampleLiNode = sampleListItemRef.current;
    if (!listNode || !sampleLiNode || sampleLiNode.offsetHeight === 0) {
      setDynamicItemsPerPage( (Array.isArray(localHistorico) && localHistorico.length > 0) ? 5 : 1);
      return;
    }
    
    // Lógica simples de fallback: 5 itens por padrão
    setDynamicItemsPerPage(5);
  }, [isModalMode, localHistorico]);

  useLayoutEffect(() => {
    if (sortedHistorico.length > 0) {
      const timer = setTimeout(calculateItemsPerPage, 50);
      return () => clearTimeout(timer);
    }
  }, [sortedHistorico.length, calculateItemsPerPage]);

  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(sortedHistorico.length / dynamicItemsPerPage));
    if (currentPage > newTotalPages) { setCurrentPage(newTotalPages); }
  }, [currentPage, sortedHistorico.length, dynamicItemsPerPage]);

  // --- ENVIO DO HUMOR ---
  const handleSubmitHumor = async (e) => {
    e.preventDefault();
    if (!humorSelecionado) { setMensagem({ type: 'error', text: "Por favor, selecione seu humor."}); return; }
    if (!userId || !empresaId || !userData) { setMensagem({ type: 'error', text: "Dados insuficientes para registro."}); return; }
    
    setIsSubmitting(true);
    const timestampAtual = new Date();
    
    // Objeto para o array histórico do usuário
    const novoRegistroHumor = {
        data: timestampAtual.toISOString().split('T')[0],
        humor: humorSelecionado,
        observacao: observacao,
        registradoEm: timestampAtual
    };

    try {
      // 1. Salvar no Perfil Privado do Usuário (Histórico Pessoal)
      const userPrivateDocRef = doc(db, 'usersprivado', userId);
      const updateData = { termometroHumor: arrayUnion(novoRegistroHumor) };
      
      // Se for resposta a uma campanha específica, atualiza o flag para não mostrar o modal novamente
      if (isModalMode && campaignId) {
          updateData.lastMoodSurveyResponseId = campaignId;
      }
      await setDoc(userPrivateDocRef, updateData, { merge: true });

      // 2. Salvar na Coleção Centralizada da Empresa (Para o Dashboard de Gestão de Humor)
      // IMPORTANTE: Isso permite que o RH veja todas as respostas, internas e externas, em um só lugar.
      await addDoc(collection(db, 'Empresas', empresaId, 'moodResponses'), {
          humor: humorSelecionado,
          observacao: observacao,
          userId: userId,
          userName: userData.name,
          userEmail: userData.email,
          isAnonymous: false,
          origem: 'plataforma', // Diferencia do link público
          createdAt: Timestamp.now(),
          data: novoRegistroHumor.data
      });

      // 3. Notificação ao Líder (Apenas se humor for negativo)
      if (humorSelecionado === 'triste' || humorSelecionado === 'muito-triste') {
        const orgNodesRef = collection(db, 'Empresas', empresaId, 'organizationChartNodes');
        const qNode = query(orgNodesRef, where("userId", "==", userId));
        const nodeSnap = await getDocs(qNode);

        if (!nodeSnap.empty && nodeSnap.docs[0].data().parentId) {
          const parentNodeId = nodeSnap.docs[0].data().parentId;
          const parentNodeDoc = await getDoc(doc(orgNodesRef, parentNodeId));

          if (parentNodeDoc.exists() && parentNodeDoc.data().userId) {
            const leaderId = parentNodeDoc.data().userId;
            
            // Cria alerta na coleção de feedbacks
            await addDoc(collection(db, 'Empresas', empresaId, 'feedbacks'), {
              tipo: 'alerta_humor',
              humor: humorSelecionado,
              autorId: userId,
              autorNome: userData.name,
              destinatarioId: leaderId,
              dataEnvio: Timestamp.now(),
              lido: false,
              empresaId: empresaId
            });
          }
        }
      }

      // Reset e Sucesso
      setHumorSelecionado('');
      setObservacao('');
      
      if (isModalMode && onSurveySubmit) {
          onSurveySubmit(); // Fecha o modal no Dashboard
      } else {
        setMensagem({ type: 'success', text: "Obrigado! Seu humor foi registrado."});
        setTimeout(() => setMensagem(null), 3000);
        // Atualiza lista local se não for modal
        setLocalHistorico(prev => [novoRegistroHumor, ...prev]);
      }

    } catch (error) {
      console.error("Erro ao registrar humor: ", error);
      setMensagem({ type: 'error', text: `Erro ao registrar: ${error.message}`});
    } finally {
        setIsSubmitting(false);
    }
  };

  const opcoesHumor = [
    { valor: 'muito-feliz', label: '😄 Muito Feliz', cor: '#4CAF50' },
    { valor: 'feliz', label: '🙂 Feliz', cor: '#8BC34A' },
    { valor: 'neutro', label: '😐 Neutro', cor: '#FFC107' },
    { valor: 'triste', label: '😟 Triste', cor: '#FF9800' },
    { valor: 'muito-triste', label: '😭 Muito Triste', cor: '#F44336' },
  ];

  const totalPages = Math.max(1, Math.ceil(sortedHistorico.length / dynamicItemsPerPage));
  const currentItems = sortedHistorico.slice((currentPage - 1) * dynamicItemsPerPage, currentPage * dynamicItemsPerPage);
  const handlePageChange = (pageNumber) => { setCurrentPage(pageNumber); };

  return (
    <div className="termometro-humor-container">
        <form onSubmit={handleSubmitHumor}>
          {!hideTitle && <p>Como você está se sentindo hoje?</p>}
          
          <div className="humor-opcoes">
            {opcoesHumor.map(opcao => (
              <button type="button" key={opcao.valor}
                className={`humor-botao ${humorSelecionado === opcao.valor ? 'selecionado' : ''}`}
                onClick={isReadOnly || isSubmitting ? undefined : () => setHumorSelecionado(opcao.valor)}
                disabled={isReadOnly || isSubmitting}
                style={{
                    borderColor: opcao.cor,
                    backgroundColor: humorSelecionado === opcao.valor ? opcao.cor : 'transparent',
                    color: humorSelecionado === opcao.valor ? 'white' : opcao.cor,
                    cursor: (isReadOnly || isSubmitting) ? 'default' : 'pointer'
                }}
                title={opcao.label.split(' ')[1]}
              >
                <span role="img" aria-label={opcao.label.split(' ')[1]}>{opcao.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>

          {!isReadOnly && humorSelecionado && (
            <textarea
              className="form-control mt-3"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Alguma observação? (opcional)"
              rows="2"
              disabled={isSubmitting}
            />
          )}

          {!isReadOnly && (
            <button
              type="submit"
              disabled={!humorSelecionado || !userId || isSubmitting}
              className="btn btn-primary btn-block submit-humor-btn mt-3"
            >
                {isSubmitting ? 'Registrando...' : 'Registrar Humor'}
            </button>
          )}
        </form>

        {mensagem && ( <p className={`feedback-message ${mensagem.type} mt-3`}>{mensagem.text}</p> )}

        {/* LISTA DE HISTÓRICO (Apenas visualização pessoal) */}
        {!isModalMode && (
          <>
            <h4 className="mt-4">Seu Histórico:</h4>
            {sortedHistorico.length === 0 ? (
                <p className="text-muted">Nenhum registro de humor ainda.</p>
            ) : (
                <>
                    <ul ref={historyListRef} className="historico-humor-lista">
                        {currentItems.map((item, index) => {
                            const humorInfo = opcoesHumor.find(o => o.valor === item.humor);
                            const dataFormatada = item.registradoEm instanceof Date 
                                ? item.registradoEm.toLocaleString('pt-BR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) 
                                : 'Data inválida';
                            const key = `${item.registradoEm?.getTime()}-${index}`;
                            
                            return (
                                <li key={key} ref={index === 0 ? sampleListItemRef : null} className="historico-humor-item">
                                    <span style={{fontWeight:'bold'}}>{dataFormatada}:</span> {humorInfo ? humorInfo.label : item.humor}
                                    {item.observacao && <span style={{fontStyle:'italic', display:'block', fontSize:'0.9em', color:'#666'}}>"{item.observacao}"</span>}
                                </li>
                            );
                        })}
                    </ul>
                    {totalPages > 1 && (
                      <div className="mt-3 d-flex justify-content-center">
                          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                      </div>
                    )}
                </>
            )}
          </>
        )}
    </div>
  );
};

export default TermometroHumor;